import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Save, Loader2, Upload, Eye, Code, X, Plus, Info, ShieldAlert } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

/**
 * Parses markdown content to extract section index from ## headers.
 * Looks for optional `tags:` line immediately after each ## header.
 */
function parseSectionIndex(content) {
  if (!content) return [];
  const lines = content.split('\n');
  const sections = [];
  let currentSection = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Match ## headers (level 2 only for sections)
    const headerMatch = line.match(/^##\s+(.+)$/);
    if (headerMatch) {
      // Close previous section
      if (currentSection) {
        currentSection.line_end = i - 1;
        currentSection.token_estimate = Math.ceil(
          lines.slice(currentSection.line_start, i).join('\n').length / 4
        );
        sections.push(currentSection);
      }

      const header = headerMatch[1].trim();
      currentSection = {
        header,
        tags: [],
        line_start: i,
        line_end: null,
        token_estimate: 0
      };

      // Check next line for tags: directive
      if (i + 1 < lines.length) {
        const tagsMatch = lines[i + 1].match(/^tags:\s*(.+)$/i);
        if (tagsMatch) {
          currentSection.tags = tagsMatch[1].split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
        }
      }

      // If no explicit tags, derive from header words
      if (currentSection.tags.length === 0) {
        currentSection.tags = header.toLowerCase()
          .replace(/[^a-z0-9\s&]/g, '')
          .split(/\s+/)
          .filter(w => w.length > 2); // skip tiny words
      }
    }
  }

  // Close last section
  if (currentSection) {
    currentSection.line_end = lines.length - 1;
    currentSection.token_estimate = Math.ceil(
      lines.slice(currentSection.line_start).join('\n').length / 4
    );
    sections.push(currentSection);
  }

  return sections;
}

/**
 * Estimates token count from text (rough: chars / 4)
 */
function estimateTokens(text) {
  if (!text) return 0;
  return Math.ceil(text.length / 4);
}

/**
 * Parses YAML frontmatter from markdown content
 */
function parseFrontmatter(content) {
  if (!content) return { frontmatter: {}, body: content };
  const match = content.match(/^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/);
  if (!match) return { frontmatter: {}, body: content };
  
  const yamlLines = match[1].split('\n');
  const frontmatter = {};
  for (const line of yamlLines) {
    const kvMatch = line.match(/^(\w[\w-]*):\s*(.+)$/);
    if (kvMatch) {
      frontmatter[kvMatch[1]] = kvMatch[2].trim();
    }
  }
  return { frontmatter, body: match[2] };
}

/**
 * Builds content with frontmatter from form fields
 */
function buildContentWithFrontmatter(name, description, body) {
  const fm = `---\nname: ${name}\ndescription: ${description}\n---\n`;
  return fm + body;
}


export default function BotSkillEditorDialog({ open, onOpenChange, skill, onSave, saving }) {
  const [activeTab, setActiveTab] = React.useState('edit');
  
  // Form state
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [fileName, setFileName] = React.useState('');
  const [content, setContent] = React.useState('');
  const [loadingStrategy, setLoadingStrategy] = React.useState('always');
  const [priority, setPriority] = React.useState(5);
  const [changeNote, setChangeNote] = React.useState('');

  // Derived state
  const sectionIndex = React.useMemo(() => parseSectionIndex(content), [content]);
  const tokenEstimate = React.useMemo(() => estimateTokens(content), [content]);

  // Initialize form when skill changes or dialog opens
  React.useEffect(() => {
    if (open) {
      if (skill) {
        // Editing existing skill
        setName(skill.name || '');
        setDescription(skill.description || '');
        setFileName(skill.file_name || '');
        setLoadingStrategy(skill.loading_strategy || 'always');
        setPriority(skill.priority || 5);
        setChangeNote('');
        
        // Extract body from content (remove frontmatter for editing)
        const { body } = parseFrontmatter(skill.content || '');
        setContent(body || skill.content || '');
      } else {
        // New skill
        setName('');
        setDescription('');
        setFileName('');
        setContent('');
        setLoadingStrategy('always');
        setPriority(5);
        setChangeNote('');
      }
      setActiveTab('edit');
    }
  }, [open, skill]);

  // Auto-generate filename from name
  React.useEffect(() => {
    if (!skill && name && !fileName) {
      const generated = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') + '.md';
      setFileName(generated);
    }
  }, [name, skill, fileName]);

  // Handle .md file upload
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const { frontmatter, body } = parseFrontmatter(text);
      
      // Auto-fill fields from frontmatter if present
      if (frontmatter.name && !name) setName(frontmatter.name);
      if (frontmatter.description && !description) setDescription(frontmatter.description);
      if (!fileName) setFileName(file.name);
      setContent(body || text);
    };
    reader.readAsText(file);
    e.target.value = ''; // reset
  };

  /**
   * ⚠️ CONTENT SECURITY SCANNER
   * Checks for patterns commonly found in malicious skills.
   * This is a first-line defense — not a substitute for human review.
   * 
   * Based on patterns identified in:
   * - Snyk ToxicSkills research (Feb 2026)
   * - SkillScan taxonomy (14 vulnerability patterns)
   * - ClawMoat detection patterns
   */
  const securityWarnings = React.useMemo(() => {
    if (!content) return [];
    const warnings = [];
    const lower = content.toLowerCase();

    // HTML comments (can hide instructions from preview but LLM reads them)
    if (/<!--[\s\S]*?-->/.test(content)) {
      warnings.push({ severity: 'high', message: 'Contains HTML comments — these are invisible in preview but read by the bot. Inspect carefully.' });
    }

    // Base64 encoded blocks (common obfuscation technique)
    if (/[A-Za-z0-9+/]{40,}={0,2}/.test(content) && (lower.includes('base64') || lower.includes('encode'))) {
      warnings.push({ severity: 'high', message: 'Contains possible base64-encoded content — may hide malicious instructions.' });
    }

    // External URLs (data exfiltration risk)
    const urlMatches = content.match(/https?:\/\/[^\s)}\]"']+/g) || [];
    if (urlMatches.length > 0) {
      warnings.push({ severity: 'medium', message: `Contains ${urlMatches.length} external URL(s) — verify these are legitimate: ${urlMatches.slice(0, 3).join(', ')}${urlMatches.length > 3 ? '...' : ''}` });
    }

    // References to credentials, env vars, API keys
    if (/api[_-]?key|secret|password|credential|token|\.env|ssh|aws/i.test(content)) {
      warnings.push({ severity: 'high', message: 'References credentials, API keys, or sensitive data — skills should NEVER access or reference secrets.' });
    }

    // Shell/code execution patterns
    if (/\b(exec|eval|subprocess|os\.system|child_process|spawn|sh -c|bash -c|curl|wget)\b/i.test(content)) {
      warnings.push({ severity: 'high', message: 'Contains shell/code execution patterns — COMPASS skills should be instruction-only, not executable.' });
    }

    // Sudo/root escalation
    if (/\bsudo\b|\bchmod\b|\bchown\b|\broot\b/i.test(content)) {
      warnings.push({ severity: 'high', message: 'Contains privilege escalation commands — this should not appear in instructional skills.' });
    }

    // Prompt injection / instruction override patterns
    if (/ignore (previous|above|all) instructions|you are now|new instructions|override|disregard/i.test(content)) {
      warnings.push({ severity: 'high', message: 'Contains possible prompt injection — attempts to override bot instructions.' });
    }

    // Zero-width characters (invisible text that can alter behavior)
    if (/[\u200B\u200C\u200D\uFEFF\u00AD]/.test(content)) {
      warnings.push({ severity: 'high', message: 'Contains zero-width/invisible characters — these can hide malicious instructions.' });
    }

    return warnings;
  }, [content]);

  const hasHighSeverity = securityWarnings.some(w => w.severity === 'high');

  const handleSave = () => {
    if (!name.trim() || !content.trim()) return;

    // Block save if high-severity warnings and user hasn't acknowledged
    if (hasHighSeverity) {
      const proceed = window.confirm(
        '⚠️ SECURITY WARNING\n\n' +
        'This skill content has been flagged with high-severity security concerns:\n\n' +
        securityWarnings.filter(w => w.severity === 'high').map(w => '• ' + w.message).join('\n') +
        '\n\nAre you sure you want to save? Only proceed if you have manually reviewed the content and confirmed it is safe.'
      );
      if (!proceed) return;
    }
    
    const fullContent = buildContentWithFrontmatter(
      name.trim(),
      description.trim(),
      content
    );

    onSave({
      name: name.trim(),
      description: description.trim(),
      file_name: fileName.trim() || (name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '.md'),
      content: fullContent,
      loading_strategy: loadingStrategy,
      priority: Number(priority),
      section_index: sectionIndex,
      token_estimate: tokenEstimate,
      change_note: changeNote.trim() || undefined,
      is_active: skill?.is_active !== false // default true for new skills
    });
  };

  const isValid = name.trim() && content.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="profile-botskilleditordialog-dialog" className="bg-slate-50 border-slate-200 text-slate-800 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Code className="w-5 h-5 text-violet-500" />
            {skill ? 'Edit Skill' : 'New Skill'}
          </DialogTitle>
        </DialogHeader>

        {/* Security warning — always visible in editor */}
        <Alert className="bg-amber-50 border-amber-200 mt-1">
          <AlertDescription className="text-xs text-amber-700">
            <strong>⚠️ Security:</strong> Skill content is injected directly into bot prompts and can alter behavior.
            If importing from external sources: review ALL content for hidden instructions, suspicious URLs, 
            references to credentials, or HTML comments. Never paste executable scripts. 
            Test on non-critical bots first.
          </AlertDescription>
        </Alert>

        <div className="space-y-4 mt-2">
          {/* Top row: Name + Description */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Name *</label>
              <Input data-testid="profile-botskilleditordialog-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. HexDB Schema Guide"
                className="mt-1 bg-white border-slate-300 text-slate-800"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">File Name</label>
              <Input data-testid="profile-botskilleditordialog-input-1"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="auto-generated.md"
                className="mt-1 bg-white border-slate-300 text-slate-800 font-mono text-sm"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">
              Description <span className="text-slate-400 normal-case font-normal">(helps bot decide when to load this skill)</span>
            </label>
            <Input data-testid="profile-botskilleditordialog-input-2"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Use when answering database queries about REEF's order, transaction, and venue tables"
              className="mt-1 bg-white border-slate-300 text-slate-800"
            />
          </div>

          {/* Settings row: Strategy + Priority */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Loading Strategy</label>
              <Select value={loadingStrategy} onValueChange={setLoadingStrategy}>
                <SelectTrigger className="mt-1 bg-white border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="always">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      Always — loaded every time
                    </span>
                  </SelectItem>
                  <SelectItem value="referenced">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-amber-500" />
                      Referenced — loaded when tags match
                    </span>
                  </SelectItem>
                  <SelectItem value="manual">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-slate-400" />
                      Manual — only when explicitly attached
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Priority</label>
              <Select value={String(priority)} onValueChange={(v) => setPriority(Number(v))}>
                <SelectTrigger className="mt-1 bg-white border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5,6,7,8,9,10].map(n => (
                    <SelectItem key={n} value={String(n)}>
                      {n} {n === 1 ? '(highest)' : n === 10 ? '(lowest)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Change Note</label>
              <Input data-testid="profile-botskilleditordialog-input-3"
                value={changeNote}
                onChange={(e) => setChangeNote(e.target.value)}
                placeholder="What changed?"
                className="mt-1 bg-white border-slate-300 text-slate-800"
              />
            </div>
          </div>

          {/* Content editor with tabs */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Skill Content *</label>
              <div className="flex items-center gap-2">
                <label htmlFor="skill-file-upload" className="cursor-pointer">
                  <Button variant="outline" size="sm" className="h-7 text-xs border-slate-300" asChild>
                    <span>
                      <Upload className="w-3 h-3 mr-1" />
                      Upload .md
                    </span>
                  </Button>
                  <input data-testid="profile-botskilleditordialog-input-4"
                    id="skill-file-upload"
                    type="file"
                    accept=".md,.txt,.markdown"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
                <Badge variant="outline" className="text-xs border-slate-300 text-slate-500 font-mono">
                  ~{tokenEstimate.toLocaleString()} tokens
                </Badge>
              </div>
            </div>
            
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-slate-200 h-8">
                <TabsTrigger value="edit" className="text-xs h-6 data-[state=active]:bg-white">
                  <Code className="w-3 h-3 mr-1" /> Edit
                </TabsTrigger>
                <TabsTrigger value="preview" className="text-xs h-6 data-[state=active]:bg-white">
                  <Eye className="w-3 h-3 mr-1" /> Preview
                </TabsTrigger>
                <TabsTrigger value="sections" className="text-xs h-6 data-[state=active]:bg-white">
                  <Info className="w-3 h-3 mr-1" /> Sections ({sectionIndex.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="edit" className="mt-2">
                <Textarea data-testid="profile-botskilleditordialog-textarea"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={`## Orders & Transactions\ntags: orders, transactions, revenue, sales\n\nThe orders table contains all customer orders...\n\n## Venues & Locations\ntags: venues, locations, kitchens\n\nVenue data is stored in...`}
                  className="bg-white border-slate-300 text-slate-800 font-mono text-sm min-h-[350px] leading-relaxed"
                />
              </TabsContent>

              <TabsContent value="preview" className="mt-2">
                <div className="bg-white p-4 rounded-lg border border-slate-300 prose prose-sm prose-slate max-w-none min-h-[350px] max-h-[350px] overflow-y-auto break-words">
                  {content ? (
                    <ReactMarkdown>{content}</ReactMarkdown>
                  ) : (
                    <p className="text-slate-400 italic">No content yet...</p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="sections" className="mt-2">
                <div className="bg-white p-4 rounded-lg border border-slate-300 min-h-[350px] max-h-[350px] overflow-y-auto">
                  {sectionIndex.length > 0 ? (
                    <div className="space-y-3">
                      <p className="text-xs text-slate-500">
                        These sections are auto-detected from <code className="bg-slate-100 px-1 rounded">##</code> headers. 
                        When loading strategy is "referenced", only sections whose tags match the query will be loaded.
                      </p>
                      {sectionIndex.map((section, idx) => (
                        <div key={idx} className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-slate-700">{section.header}</h4>
                            <Badge variant="outline" className="text-xs border-slate-300 text-slate-500 font-mono">
                              ~{section.token_estimate} tokens
                            </Badge>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {section.tags.map((tag, tidx) => (
                              <Badge key={tidx} variant="secondary" className="text-xs bg-violet-100 text-violet-700 border-none">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            Lines {section.line_start + 1}–{section.line_end + 1}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-slate-400 text-sm">
                        No sections detected. Use <code className="bg-slate-100 px-1 rounded">## Header</code> in your content to create loadable sections.
                      </p>
                      <p className="text-slate-400 text-xs mt-2">
                        Optionally add <code className="bg-slate-100 px-1 rounded">tags: keyword1, keyword2</code> on the line below each header.
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Security scan results */}
          {securityWarnings.length > 0 && (
            <div className="space-y-1.5 p-3 bg-rose-50 rounded-lg border border-rose-200">
              <p className="text-xs font-semibold text-rose-700 flex items-center gap-1">
                <ShieldAlert className="w-3.5 h-3.5" />
                Security Scan — {securityWarnings.length} issue{securityWarnings.length !== 1 ? 's' : ''} detected
              </p>
              {securityWarnings.map((warning, idx) => (
                <p key={idx} className={`text-xs ${warning.severity === 'high' ? 'text-rose-600 font-medium' : 'text-amber-600'}`}>
                  {warning.severity === 'high' ? '🔴' : '🟡'} {warning.message}
                </p>
              ))}
            </div>
          )}

          {/* Save button */}
          <div className="flex items-center justify-between pt-2 border-t border-slate-200">
            <div className="text-xs text-slate-400">
              {sectionIndex.length > 0 && (
                <span>{sectionIndex.length} section{sectionIndex.length !== 1 ? 's' : ''} detected</span>
              )}
            </div>
            <div className="flex gap-2">
              <Button data-testid="profile-cancel-btn" variant="outline" onClick={() => onOpenChange(false)} className="border-slate-300">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!isValid || saving}
                className="bg-violet-600 hover:bg-violet-700 text-white"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" />{skill ? 'Update Skill' : 'Create Skill'}</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}